// ===================================================================
// PA GRANTS — Dashboard
// Kanban pipeline view with disaster context, stats, and project cards
// ===================================================================

import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Paper, Text, Title, Group, Stack, Badge, Button, SimpleGrid, Loader, Center,
  Table, Progress,
} from '../../components/gab/GabComponents';
import { IconEdit, IconClock, IconReceipt, IconAlertCircle } from '@tabler/icons-react';
import { usePAGrantsStore } from '../../store/paGrantsStore';
import { useRoleStore } from '../../store/roleStore';
import { DAMAGE_CATEGORY_COLORS, type DamageCategory } from '../../types/pagrants';

const APPLICANT_SLUGS = ['pa_applicant_mgr', 'pa_applicant_staff', 'pa_applicant_viewer'];

const fmt = (n: number) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

const PHASE_COLUMNS = [
  { key: 'formulation', label: 'Formulation', color: '#339af0' },
  { key: 'review', label: 'In Review', color: '#fcc419' },
  { key: 'obligation', label: 'Obligated', color: '#099268' },
  { key: 'work_in_progress', label: 'Work in Progress', color: '#f76707' },
  { key: 'closeout', label: 'Closeout', color: '#7c3aed' },
] as const;

const CATEGORIES: DamageCategory[] = ['A', 'B', 'C', 'D', 'E', 'F', 'G'];

export default function PAGrantsDashboard() {
  const navigate = useNavigate();
  const {
    organizations, disasters, projects, timeExtensions, reimbursementRequests,
    loadingOrgs, loadingDisasters, loadingProjects,
    fetchAll,
  } = usePAGrantsStore();

  const effectiveRole = useRoleStore(s => s.demoRoleOverride ?? s.myRole);
  const isApplicantRole = APPLICANT_SLUGS.includes(effectiveRole?.roleSlug ?? '');
  // Disaster is managed by the layout's selector — read from store
  const activeDisasterId = usePAGrantsStore(s => s.activeDisasterId);

  // Always refresh data on mount (component remounts on route change)
  useEffect(() => { fetchAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isLoading = loadingOrgs || loadingDisasters || loadingProjects;

  const selectedDisaster = useMemo(
    () => activeDisasterId ? (disasters.find(d => String(d.Rid) === activeDisasterId) ?? null) : null,
    [disasters, activeDisasterId],
  );

  // Projects for selected disaster
  const disasterProjects = useMemo(
    () => selectedDisaster
      ? projects.filter(p => p.disasterId === String(selectedDisaster.Rid) && p.workType !== 'hmgp')
      : [],
    [projects, selectedDisaster],
  );

  // Org lookup map
  const orgMap = useMemo(() => {
    const m = new Map<string, string>();
    organizations.forEach(o => { m.set(String(o.Rid), o.orgName); });
    return m;
  }, [organizations]);

  // Stats
  const stats = useMemo(() => {
    const smallCount = disasterProjects.filter(p => p.projectSize === 'small').length;
    const largeCount = disasterProjects.filter(p => p.projectSize === 'large').length;
    const totalObligated = disasterProjects.reduce((s, p) => s + (parseFloat(p.obligatedAmount) || 0), 0);
    const totalEstimated = disasterProjects.reduce((s, p) => s + (parseFloat(p.estimatedCost) || 0), 0);
    const pendingReview = disasterProjects.filter(p => p.status === 'in_review').length;

    const now = Date.now();
    const thirtyDays = 30 * 24 * 60 * 60 * 1000;
    const deadlineSoon = disasterProjects.filter(p => {
      if (!p.deadline) return false;
      const d = new Date(p.deadline).getTime();
      return !isNaN(d) && d > now && d - now < thirtyDays;
    }).length;

    return {
      applicants: organizations.length,
      projectCount: disasterProjects.length,
      smallCount, largeCount,
      totalObligated, totalEstimated,
      pendingReview, deadlineSoon,
    };
  }, [disasterProjects, organizations]);

  // Phase counts for pipeline bar
  const phaseCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const col of PHASE_COLUMNS) counts[col.key] = 0;
    for (const p of disasterProjects) {
      if (counts[p.phase] !== undefined) counts[p.phase]++;
    }
    return counts;
  }, [disasterProjects]);

  // Summary table: projects grouped by applicant
  const applicantSummary = useMemo(() => {
    const byOrg = new Map<string, { orgName: string; orgId: string; projects: number; estimated: number; obligated: number; drawnDown: number; phases: Record<string, number> }>();
    for (const p of disasterProjects) {
      const orgId = p.organizationId;
      if (!byOrg.has(orgId)) {
        byOrg.set(orgId, {
          orgName: orgMap.get(orgId) || 'Unknown',
          orgId,
          projects: 0, estimated: 0, obligated: 0, drawnDown: 0,
          phases: {},
        });
      }
      const row = byOrg.get(orgId)!;
      row.projects++;
      row.estimated += parseFloat(p.estimatedCost) || 0;
      row.obligated += parseFloat(p.obligatedAmount) || 0;
      row.drawnDown += parseFloat(p.drawdownAmount) || 0;
      row.phases[p.phase] = (row.phases[p.phase] || 0) + 1;
    }
    return Array.from(byOrg.values()).sort((a, b) => b.estimated - a.estimated);
  }, [disasterProjects, orgMap]);

  // Category breakdown
  const categoryBreakdown = useMemo(() =>
    CATEGORIES.map(cat => {
      const catProjects = disasterProjects.filter(p => p.category === cat);
      const total = catProjects.reduce((s, p) => s + (parseFloat(p.estimatedCost) || 0), 0);
      return { cat, count: catProjects.length, total };
    }).filter(r => r.count > 0),
    [disasterProjects],
  );

  // Upcoming deadlines
  const upcomingDeadlines = useMemo(() => {
    const now = Date.now();
    return disasterProjects
      .filter(p => p.deadline)
      .map(p => {
        const dl = new Date(p.deadline).getTime();
        if (isNaN(dl)) return null;
        const daysUntil = (dl - now) / (24 * 60 * 60 * 1000);
        return { ...p, deadlineMs: dl, daysUntil };
      })
      .filter(Boolean)
      .sort((a, b) => a!.deadlineMs - b!.deadlineMs)
      .slice(0, 10) as Array<typeof disasterProjects[number] & { deadlineMs: number; daysUntil: number }>;
  }, [disasterProjects]);

  const showLoader = isLoading && organizations.length === 0 && disasters.length === 0;

  // ── Disaster context bar ──────────────────────────────────────
  const disasterBar = selectedDisaster ? (
    <Group justify="space-between" wrap="wrap">
      <div>
        <Title order={3} c="black">PA Grants</Title>
        <Group gap="xs" mb={4}>
          <Text size="sm" fw={600}>{selectedDisaster.disasterName}</Text>
          {selectedDisaster.disasterNumber && (
            <Badge variant="light" color="blue" size="sm">#{selectedDisaster.disasterNumber}</Badge>
          )}
        </Group>
        <Group gap="lg">
          <Text size="xs" c="dimmed">
            Declaration: {selectedDisaster.declarationType?.toUpperCase() || 'N/A'} &mdash; {selectedDisaster.declarationDate || 'No date'}
          </Text>
          <Text size="xs" c="dimmed">
            Federal Share: {selectedDisaster.federalSharePct}%
          </Text>
          {selectedDisaster.largeProjectThreshold && (
            <Text size="xs" c="dimmed">
              Large Project Threshold: {/^\d/.test(selectedDisaster.largeProjectThreshold) ? fmt(parseFloat(selectedDisaster.largeProjectThreshold) || 0) : selectedDisaster.largeProjectThreshold}
            </Text>
          )}
        </Group>
      </div>
      <Button
        variant="light" size="xs"
        leftSection={<IconEdit size={14} />}
        onClick={() => navigate('/pagrants/pa/disasters')}
      >
        Edit Disaster
      </Button>
    </Group>
  ) : (
    <Paper p="lg" radius="md" style={{ background: '#f1f3f5', textAlign: 'center' }}>
      <Text size="lg" fw={600} c="dimmed" mb="sm">No Disaster Selected</Text>
      <Text size="sm" c="dimmed" mb="md">Select a disaster above or create one to start tracking PA projects.</Text>
      <Button onClick={() => navigate('/pagrants/pa/disasters')}>Manage Disasters</Button>
    </Paper>
  );

  // ── Stats row ─────────────────────────────────────────────────
  const statsRow = (
    <SimpleGrid cols={{ base: 2, sm: 5 }} spacing="sm">
      <Paper p="sm" radius="md" withBorder ta="center" style={{ borderTop: '4px solid var(--color-primary-800)' }}>
        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Applicants</Text>
        <Text fz={28} fw={700}>{stats.applicants}</Text>
      </Paper>
      <Paper p="sm" radius="md" withBorder ta="center" style={{ borderTop: '4px solid #07963F' }}>
        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Projects</Text>
        <Text fz={28} fw={700}>{stats.projectCount}</Text>
        <Text size="xs" c="dimmed">{stats.smallCount} small / {stats.largeCount} large</Text>
      </Paper>
      <Paper p="sm" radius="md" withBorder ta="center" style={{ borderTop: '4px solid var(--color-primary-700)' }}>
        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Obligated</Text>
        <Text fz={28} fw={700}>{fmt(stats.totalObligated)}</Text>
        <Text size="xs" c="dimmed">of {fmt(stats.totalEstimated)} est.</Text>
      </Paper>
      <Paper p="sm" radius="md" withBorder ta="center" style={{ borderTop: '4px solid var(--color-error)' }}>
        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Pending Review</Text>
        <Text fz={28} fw={700}>{stats.pendingReview}</Text>
      </Paper>
      <Paper p="sm" radius="md" withBorder ta="center" style={{ borderTop: '4px solid #A67103' }}>
        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Deadlines &lt; 30 Days</Text>
        <Text fz={28} fw={700}>{stats.deadlineSoon}</Text>
      </Paper>
    </SimpleGrid>
  );

  // ── Action Items: pending TEs and RFRs ───────────────────────
  const pendingTEs = useMemo(() =>
    timeExtensions.filter(t => ['submitted', 'forwarded_to_fema'].includes(t.status) && (!selectedDisaster || t.disasterId === String(selectedDisaster.Rid))),
    [timeExtensions, selectedDisaster],
  );
  const pendingRFRs = useMemo(() =>
    reimbursementRequests.filter(r => ['submitted', 'under_review'].includes(r.status) && (!selectedDisaster || r.disasterId === String(selectedDisaster.Rid))),
    [reimbursementRequests, selectedDisaster],
  );
  const projMapLocal = useMemo(() => new Map(projects.map(p => [String(p.Rid), p])), [projects]);

  const actionItems = (
    <Paper p="md" radius="md" withBorder style={{ borderLeft: '3px solid var(--mantine-color-orange-5)' }}>
      <Group gap="xs" mb="sm">
        <IconAlertCircle size={18} color="var(--mantine-color-orange-6)" />
        <Text size="sm" fw={700}>Action Items</Text>
        {(pendingTEs.length + pendingRFRs.length) > 0 && (
          <Badge size="xs" color="orange">{pendingTEs.length + pendingRFRs.length}</Badge>
        )}
      </Group>
      <Stack gap="xs">
        {pendingTEs.map(te => {
          const proj = projMapLocal.get(te.projectId);
          return (
            <Group key={te.Rid} justify="space-between" py={4} style={{ borderBottom: '1px solid #f1f3f5', cursor: 'pointer' }}
              onClick={() => navigate('/pagrants/pa/time-extensions')}>
              <Group gap="xs">
                <IconClock size={14} color="var(--mantine-color-blue-6)" />
                <Text size="sm">Time Extension Request — <Text span fw={600}>{proj?.projectNumber || '?'}</Text> {proj?.projectTitle || ''}</Text>
              </Group>
              <Group gap="xs">
                <Text size="xs" c="dimmed">{te.extensionMonths} mo requested</Text>
                <Badge size="xs" color="blue" variant="light">Awaiting Approval</Badge>
              </Group>
            </Group>
          );
        })}
        {pendingRFRs.map(rfr => {
          const orgName = orgMap.get(rfr.organizationId) || 'Unknown';
          return (
            <Group key={rfr.Rid} justify="space-between" py={4} style={{ borderBottom: '1px solid #f1f3f5', cursor: 'pointer' }}
              onClick={() => navigate('/pagrants/pa/reimbursements')}>
              <Group gap="xs">
                <IconReceipt size={14} color="var(--mantine-color-teal-6)" />
                <Text size="sm">Reimbursement <Text span fw={600}>{rfr.requestNumber}</Text> — {orgName}</Text>
              </Group>
              <Group gap="xs">
                <Text size="xs" fw={600}>{fmt(parseFloat(rfr.requestedAmount) || 0)}</Text>
                <Badge size="xs" color={rfr.status === 'under_review' ? 'indigo' : 'blue'} variant="light">
                  {rfr.status === 'under_review' ? 'Under Review' : 'Awaiting Review'}
                </Badge>
              </Group>
            </Group>
          );
        })}
        {pendingTEs.length === 0 && pendingRFRs.length === 0 && (
          <Text size="sm" c="dimmed">No pending time extensions or reimbursement requests to review.</Text>
        )}
      </Stack>
    </Paper>
  );

  // ── Phase Pipeline Bar ──────────────────────────────────────
  const pipelineBar = (
    <Paper withBorder p="md" radius="md">
      <Group justify="space-between" mb="sm">
        <Text size="sm" fw={700}>Project Pipeline</Text>
        {!isApplicantRole && (
          <Button variant="light" size="xs" onClick={() => navigate('/pagrants/pa/pipeline')}>View Board</Button>
        )}
      </Group>
      <Group gap="md" wrap="wrap">
        {PHASE_COLUMNS.map(col => {
          const count = phaseCounts[col.key] || 0;
          return (
            <Paper key={col.key} p="xs" radius="sm" style={{ textAlign: 'center', minWidth: 100, flex: 1 }}>
              <Text size="xl" fw={700}>{count}</Text>
              <Badge size="xs" color={col.color} variant="light">{col.label}</Badge>
            </Paper>
          );
        })}
      </Group>
      {disasterProjects.length > 0 && (
        <Progress.Root size="lg" mt="sm" radius="sm">
          {PHASE_COLUMNS.map(col => {
            const count = phaseCounts[col.key] || 0;
            const pct = disasterProjects.length > 0 ? (count / disasterProjects.length) * 100 : 0;
            return pct > 0 ? (
              <Progress.Section key={col.key} value={pct} color={col.color}>
                {pct > 8 && <Progress.Label>{count}</Progress.Label>}
              </Progress.Section>
            ) : null;
          })}
        </Progress.Root>
      )}
    </Paper>
  );

  // ── Applicant Summary Table ─────────────────────────────────
  const applicantTable = (
    <Paper withBorder radius="md">
      <Group justify="space-between" p="sm" pb={0}>
        <Text size="sm" fw={700}>Projects by Applicant</Text>
        <Text size="xs" c="dimmed">{applicantSummary.length} organizations</Text>
      </Group>
      <div style={{ overflowX: 'auto', maxHeight: 480, overflowY: 'auto' }}>
        <Table striped highlightOnHover stickyHeader>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Applicant</Table.Th>
              <Table.Th style={{ textAlign: 'center' }}>Projects</Table.Th>
              {PHASE_COLUMNS.map(col => (
                <Table.Th key={col.key} style={{ textAlign: 'center' }}>
                  <Badge size="xs" color={col.color} variant="light">{col.label}</Badge>
                </Table.Th>
              ))}
              <Table.Th style={{ textAlign: 'right' }}>Estimated</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>Obligated</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>Drawn Down</Table.Th>
              <Table.Th style={{ width: 120 }}>Progress</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {applicantSummary.length === 0 && (
              <Table.Tr><Table.Td colSpan={10} style={{ textAlign: 'center', padding: 24 }}><Text c="dimmed" size="sm">No projects for this disaster.</Text></Table.Td></Table.Tr>
            )}
            {applicantSummary.map(row => {
              const drawPct = row.obligated > 0 ? Math.round((row.drawnDown / row.obligated) * 100) : 0;
              return (
                <Table.Tr key={row.orgId} style={{ cursor: 'pointer' }} onClick={() => navigate(`/pagrants/pa/applicants/${row.orgId}`)}>
                  <Table.Td fw={500}>{row.orgName}</Table.Td>
                  <Table.Td style={{ textAlign: 'center' }}>{row.projects}</Table.Td>
                  {PHASE_COLUMNS.map(col => (
                    <Table.Td key={col.key} style={{ textAlign: 'center' }}>
                      {row.phases[col.key] ? <Badge size="xs" color={col.color}>{row.phases[col.key]}</Badge> : <Text size="xs" c="dimmed">—</Text>}
                    </Table.Td>
                  ))}
                  <Table.Td style={{ textAlign: 'right' }}>{fmt(row.estimated)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{fmt(row.obligated)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{fmt(row.drawnDown)}</Table.Td>
                  <Table.Td>
                    <Group gap={4}>
                      <Progress value={drawPct} size="sm" style={{ flex: 1 }} color={drawPct >= 100 ? 'green' : 'blue'} />
                      <Text size="xs" c="dimmed">{drawPct}%</Text>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </div>
    </Paper>
  );

  // ── Category summary (categories as columns) ───────────────────
  const categoryTable = (
    <Paper p="sm" radius="md" withBorder>
      <Text size="sm" fw={700} mb="xs">Projects by Damage Category</Text>
      {categoryBreakdown.length === 0 ? (
        <Text size="xs" c="dimmed">No projects to display.</Text>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e9ecef' }}>
                <th style={{ padding: '6px 8px', textAlign: 'left' }}></th>
                {categoryBreakdown.map(r => (
                  <th key={r.cat} style={{ padding: '6px 8px', textAlign: 'center' }}>
                    <Badge size="xs" style={{ background: DAMAGE_CATEGORY_COLORS[r.cat], color: '#fff' }}>{r.cat}</Badge>
                  </th>
                ))}
                <th style={{ padding: '6px 8px', textAlign: 'center', fontWeight: 700 }}>Total</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ borderBottom: '1px solid #f1f3f5' }}>
                <td style={{ padding: '6px 8px', fontWeight: 600 }}>Projects</td>
                {categoryBreakdown.map(r => (
                  <td key={r.cat} style={{ padding: '6px 8px', textAlign: 'center' }}>{r.count}</td>
                ))}
                <td style={{ padding: '6px 8px', textAlign: 'center', fontWeight: 700 }}>{categoryBreakdown.reduce((s, r) => s + r.count, 0)}</td>
              </tr>
              <tr>
                <td style={{ padding: '6px 8px', fontWeight: 600 }}>Estimated</td>
                {categoryBreakdown.map(r => (
                  <td key={r.cat} style={{ padding: '6px 8px', textAlign: 'center' }}>{fmt(r.total)}</td>
                ))}
                <td style={{ padding: '6px 8px', textAlign: 'center', fontWeight: 700 }}>{fmt(categoryBreakdown.reduce((s, r) => s + r.total, 0))}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </Paper>
  );

  // ── Upcoming Deadlines (full width) ───────────────────────────
  const deadlinesSection = (
    <Paper p="sm" radius="md" withBorder>
      <Text size="sm" fw={700} mb="xs">Upcoming Deadlines &amp; Tasks</Text>
      {upcomingDeadlines.length === 0 ? (
        <Text size="xs" c="dimmed">No upcoming deadlines.</Text>
      ) : (
        <div style={{ maxHeight: 300, overflowY: 'auto' }}>
          <Stack gap={3}>
            {upcomingDeadlines.map(p => {
              const orgName = orgMap.get(p.organizationId) || 'Unknown Org';
              let badgeColor = 'green';
              let badgeLabel = new Date(p.deadline).toLocaleDateString();
              if (p.daysUntil < 0) { badgeColor = 'red'; badgeLabel = 'Overdue'; }
              else if (p.daysUntil < 7) { badgeColor = 'yellow'; }
              return (
                <Group key={p.Rid} justify="space-between" py={3}
                  style={{ borderBottom: '1px solid #f1f3f5', cursor: 'pointer' }}
                  onClick={() => navigate(`/pagrants/pa/applicants/${p.organizationId}/projects/${p.Rid}`)}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <Text size="xs" fw={600} truncate>{p.projectTitle}</Text>
                    <Text size="xs" c="dimmed">{orgName} · {p.projectNumber}</Text>
                  </div>
                  <Badge size="xs" color={badgeColor} variant="light">{badgeLabel}</Badge>
                </Group>
              );
            })}
          </Stack>
        </div>
      )}
    </Paper>
  );

  if (showLoader) return <Center h={300}><Loader size="lg" /></Center>;

  return (
    <Stack gap="md">
      {disasterBar}
      {statsRow}
      {actionItems}
      {pipelineBar}
      {categoryTable}
      {applicantTable}
      {deadlinesSection}
    </Stack>
  );
}
